import React, { useState } from 'react';
import { Check, ChevronRight, ExternalLink, MapPin, Plus } from 'lucide-react';
import { RobbieMark } from '../ui';
import { SupplierView } from './discoveryModel';
import { websiteLabelOf } from './supplierWebsite';
import { MetricTone, formatLead, leadContextLabel, leadTone, priceDeltaLabel, priceTone } from './discoveryInsights';
import TrustChip from './TrustChip';
import PartNumberChip from './PartNumberChip';
import ContactEmailBadge from '../suppliers/ContactEmailBadge';
import SupplierDetailBlocks from './SupplierDetailBlocks';
import SupplierFlags from './SupplierFlags';
import SpecMismatchNote from './SpecMismatchNote';
import ListingRows from './ListingRows';
import { useReadOnly } from '../../contexts/WizardAccessContext';

/**
 * "Robbie's picks" card (v2.1 mock): rank badge, name, trust chip, location,
 * shortlist toggle, reasoning line, and the three-metric row — UNIT PRICE
 * (delta vs target), LEAD TIME (schedule context), NEGOTIABLE TO (with its
 * basis). Deltas and slack are frontend-computed from the buyer's own
 * requirements; a metric without data renders "—" and computed context only
 * appears when its inputs exist (honest-UI rule).
 *
 * A supplier with several matching products is still ONE card: it leads with
 * its best product's numbers, the header says "2 products", and the
 * shortlist tick moves onto each compact product row under it.
 */
interface SupplierPickCardProps {
  supplier: SupplierView;
  shortlisted: boolean;
  onToggleShortlist: () => void;
  /** Every matching product of this supplier, best first; rows render when there are two or more. */
  listings?: SupplierView[];
  tickedKeys?: readonly string[];
  onToggleListing?: (listingKey: string) => void;
  /** Short part name for the button ("Shortlist for Brushless motor"). */
  partLabel?: string;
  quantity?: number;
  targetPrice?: number;
  /** Outer edge of the buyer's desired lead window, in weeks. */
  leadWindowWeeks?: number;
  requiredCompliance?: string[];
  onAddToFirstMessage?: (note: string) => void;
}

const TONE_TEXT: Record<MetricTone, string> = {
  success: 'text-success-ink',
  warn: 'text-warn-ink',
  ink: 'text-ink',
};

const MetricCell: React.FC<{
  label: React.ReactNode;
  value: string;
  sub?: string;
  tone?: MetricTone;
  robbie?: boolean;
  /** Grid placement from the caller — a phone gives one cell the full row. */
  className?: string;
}> = ({ label, value, sub, tone = 'ink', robbie = false, className = '' }) => (
  <div className={`px-s4 py-[7px] ${robbie ? 'bg-ai-chip' : 'bg-surface'} ${className}`}>
    <div className="flex items-center gap-s1 text-[10px] font-semibold uppercase tracking-[0.06em] text-ink-muted">
      {label}
    </div>
    <div className={`font-mono text-[15px] font-semibold leading-[1.3] ${robbie ? 'text-robbie-ink' : TONE_TEXT[tone]}`}>
      {value}
    </div>
    {/* One line on a desktop — model output can be verbose and hover carries
        the rest. A phone has no hover, so the same clamp would simply delete
        the sentence; there it wraps instead. */}
    {sub && (
      <div className="truncate text-[10px] text-ink-muted max-md:overflow-visible max-md:whitespace-normal" title={sub}>
        {sub}
      </div>
    )}
  </div>
);

export const SupplierPickCard: React.FC<SupplierPickCardProps> = ({
  supplier,
  shortlisted,
  onToggleShortlist,
  partLabel,
  quantity,
  targetPrice,
  leadWindowWeeks,
  requiredCompliance,
  onAddToFirstMessage,
  listings,
  tickedKeys = [],
  onToggleListing,
}) => {
  const readOnly = useReadOnly();
  const [expanded, setExpanded] = useState(false);
  const multi = Boolean(listings && listings.length > 1 && onToggleListing);

  const priceValue =
    supplier.quotedPrice != null
      ? `${supplier.currencySymbol}${supplier.quotedPrice.toLocaleString()}`
      : supplier.priceLabel ?? '—';
  const leadValue = supplier.leadTimeWeeks != null ? formatLead(supplier.leadTimeWeeks) : supplier.leadTimeLabel ?? '—';

  return (
    <div className="rounded-[9px] border border-robbie-border-soft bg-surface px-[16px] py-[14px]">
      <div className="flex flex-wrap items-center gap-s3">
        {supplier.rank != null && (
          <span
            className={`rounded-[5px] px-[7px] py-[2px] text-[9.5px] font-extrabold tracking-[0.04em] text-white ${
              supplier.rank === 1 ? 'bg-robbie' : 'bg-robbie/70'
            }`}
          >
            #{supplier.rank}
          </span>
        )}
        <span className="text-[14px] font-bold text-ink">{supplier.name}</span>
        <TrustChip score={supplier.trustScore} trust={supplier.trust} />
        {supplier.location && (
          <span className="inline-flex items-center gap-s1 text-[11px] text-ink-secondary">
            <MapPin className="h-[11px] w-[11px]" />
            {supplier.location}
          </span>
        )}
        <PartNumberChip check={supplier.partNumberCheck} />
        <SupplierFlags supplier={supplier} />
        <ContactEmailBadge
          variant="pill"
          hasContactEmail={supplier.hasContactEmail}
          findingContact={supplier.findingContact}
          noEmailTitle="No contact email found in research — outreach will ask for one"
        />
        {multi ? (
          <span className="ml-auto whitespace-nowrap text-meta font-semibold text-ink-secondary">{listings!.length} products</span>
        ) : readOnly ? (
          shortlisted && (
            <span className="ml-auto flex shrink-0 items-center gap-s2 rounded-control border border-success-border bg-success-bg px-[14px] py-[7px] font-sans text-body-sm font-semibold text-success-ink">
              <Check className="h-3 w-3" />
              Shortlisted
            </span>
          )
        ) : (
          <button
            type="button"
            onClick={onToggleShortlist}
            className={`ml-auto flex shrink-0 items-center gap-s2 rounded-control border px-[14px] py-[7px] font-sans text-body-sm font-semibold ${
              shortlisted
                ? 'border-success-border bg-success-bg text-success-ink'
                : 'border-line-strong bg-surface text-ink-body hover:border-action hover:text-action'
            }`}
          >
            {shortlisted ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
            {shortlisted ? 'Shortlisted' : partLabel ? `Shortlist for ${partLabel}` : 'Shortlist'}
          </button>
        )}
      </div>

      <SpecMismatchNote mismatches={supplier.specMismatches ?? []} className="mt-[7px]" />

      {supplier.reason && (
        <p className="m-0 mt-[7px] text-[12.5px] leading-[1.55] text-ink-secondary">{supplier.reason}</p>
      )}

      {multi && listings && onToggleListing && (
        <ListingRows listings={listings} tickedKeys={tickedKeys} onToggle={onToggleListing} className="mt-s2" />
      )}

      <div className="mt-s4 grid grid-cols-[repeat(auto-fit,minmax(88px,1fr))] gap-px overflow-hidden rounded-control border border-robbie-border-soft bg-robbie-border-soft max-md:grid-cols-2">
        <MetricCell
          label="Unit price"
          value={priceValue}
          sub={priceDeltaLabel(supplier.quotedPrice, targetPrice)}
          tone={priceTone(supplier.quotedPrice, targetPrice)}
        />
        <MetricCell
          label="Lead time"
          value={leadValue}
          sub={leadContextLabel(supplier.leadTimeWeeks, leadWindowWeeks)}
          tone={leadTone(supplier.leadTimeWeeks, leadWindowWeeks)}
        />
        <MetricCell
          label={
            <>
              <RobbieMark size="xxs" aria-label="Robbie" />
              Negotiable to
            </>
          }
          value={supplier.negotiableTo?.price != null ? `${supplier.currencySymbol}${supplier.negotiableTo.price.toLocaleString()}` : '—'}
          sub={supplier.negotiableTo?.basis}
          robbie
          className="max-md:col-span-2"
        />
      </div>

      <div className="mt-[9px] flex items-center gap-[12px]">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center gap-s1 font-sans text-meta font-semibold text-action hover:text-action-hover"
        >
          <ChevronRight className={`h-3 w-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          {expanded ? 'Hide details' : 'View details'}
        </button>
        {supplier.websiteUrl && (
          <a
            href={supplier.websiteUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-s1 text-meta text-ink-muted hover:text-ink-secondary"
          >
            <ExternalLink className="h-[11px] w-[11px]" /> {websiteLabelOf(supplier.websiteKind)}
          </a>
        )}
      </div>

      {expanded && (
        <SupplierDetailBlocks
          supplier={supplier}
          quantity={quantity}
          requiredCompliance={requiredCompliance}
          onAddToFirstMessage={onAddToFirstMessage}
        />
      )}
    </div>
  );
};

export default SupplierPickCard;
